import { useEffect, useState } from "react";
import axios from "axios";
import {
  INVENTORY_EXPORT_RESTRICTION_STATUS,
} from "../Constants/Config";
import { useAuthDetails } from "../Common/cookiesHelper";
import { ciUrl } from "../Constants/nodeCi";

// merchant_id -> true (restricted) | false (allowed). Shared by the side menu
// and the export page so switching pages does not refetch.
const statusCache = new Map();

export const clearInventoryExportRestrictionCache = () => statusCache.clear();

/**
 * Inventory export status for the given stores, as set by the superadmin
 * under Inventory Export. Returns { restrictedMap, loading } where
 * restrictedMap[merchant_id] === true means the store is Restricted.
 * Stores not yet loaded (or on request failure) are absent from the map.
 */
const useInventoryExportRestriction = (merchantIds = []) => {
  const { userTypeData } = useAuthDetails();
  const ids = [...new Set(merchantIds.filter(Boolean))].sort();
  const key = ids.join(",");

  const fromCache = () =>
    Object.fromEntries(
      ids.filter((id) => statusCache.has(id)).map((id) => [id, statusCache.get(id)])
    );

  const [restrictedMap, setRestrictedMap] = useState(fromCache);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const missing = ids.filter((id) => !statusCache.has(id));
    if (!missing.length || !userTypeData?.token) {
      setRestrictedMap(fromCache());
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    axios
      .post(
        ciUrl(INVENTORY_EXPORT_RESTRICTION_STATUS),
        {
          merchant_ids: missing.join(","),
          token_id: userTypeData?.token_id,
          login_type: userTypeData?.login_type,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userTypeData?.token}`,
          },
        }
      )
      .then((response) => {
        if (response?.data?.status && response.data.result) {
          Object.entries(response.data.result).forEach(([id, restricted]) =>
            statusCache.set(id, Boolean(restricted))
          );
        }
      })
      .catch(() => {
        // The export API enforces the restriction server-side; the UI just
        // falls back to showing the export option.
      })
      .finally(() => {
        if (!cancelled) {
          setRestrictedMap(fromCache());
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, userTypeData?.token]);

  return { restrictedMap, loading };
};

export default useInventoryExportRestriction;
